import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Stack, Typography } from '@mui/material'
import { visuallyHidden } from '@mui/utils'
import type { FC } from 'react'
import { useTranslation } from 'react-i18next'

import { COLORS, DividerWithLabel, ICONS, LinkUnstyled, unit } from '@hcr/ui'

import { APP_CONFIG, HTTP_CONFIG, LINK_CONFIG } from '../../../../../configs'
import { useConsentSettings } from '../../../../../contexts'
import { Path } from '../../../../../models/navigation'
import { to } from '../../../../../utils/navigation'
import { Icon, Link } from '../components'

export const Settings: FC = () => {
  const { t } = useTranslation()
  const consentSettings = useConsentSettings()

  return (
    <Stack tabIndex={-1} component='li'>
      <DividerWithLabel component='h2'>{t('account.settings')}</DividerWithLabel>
      <Link onClick={consentSettings.open}>
        <Typography variant='labelM'>{t('about.change-consent-settings')}</Typography>
        <Icon aria-hidden={true} icon={ICONS.farChevronRight} />
      </Link>
      <Link
        component={LinkUnstyled as React.ElementType}
        to={LINK_CONFIG.serviceTerms}
        key={t('about.service-terms')}
        target='_blank'
        rel='external'
      >
        <Typography variant='labelM'>{t('about.service-terms')}</Typography>
        <Icon aria-hidden={true} icon={ICONS.farArrowUpRightFromSquare} />
        <Typography sx={visuallyHidden}>{t('navigation.opens-in-a-new-tab')}</Typography>
      </Link>
      <Link
        component={LinkUnstyled as React.ElementType}
        to={LINK_CONFIG.privacyPolicy}
        key={t('about.privacy-policy')}
        target='_blank'
        rel='external'
      >
        <Typography variant='labelM'>{t('about.privacy-policy')}</Typography>
        <Icon aria-hidden={true} icon={ICONS.farArrowUpRightFromSquare} />
        <Typography sx={visuallyHidden}>{t('navigation.opens-in-a-new-tab')}</Typography>
      </Link>
      <Link component={LinkUnstyled} to={LINK_CONFIG.accessibilityStatement} target='_blank' rel='external'>
        <Typography variant='labelM'>{t('about.accessibility-statement')}</Typography>
        <FontAwesomeIcon icon={ICONS.farArrowUpRightFromSquare} />
      </Link>
      <Stack gap={unit(1)} padding={`${unit(4)} ${unit(7)}`} borderBottom={`${unit(0.25)} solid ${COLORS.vaalea[600]}`}>
        <Typography component='dt' variant='labelM'>
          {t('about.version')}
        </Typography>
        <Typography component='dd' variant='bodyM'>
          {APP_CONFIG.version}
        </Typography>
      </Stack>
      <Link
        component={LinkUnstyled as React.ElementType}
        to={APP_CONFIG.thirdPartySoftwareLicensesPath}
        target='_blank'
        rel='external'
        $disableBorderBottom={!HTTP_CONFIG.API.Consumer.useMocks}
      >
        <Typography variant='labelM'>{t('about.third-party-software-licenses')}</Typography>
        <Icon aria-hidden={true} icon={ICONS.farArrowUpRightFromSquare} />
        <Typography sx={visuallyHidden}>{t('navigation.opens-in-a-new-tab')}</Typography>
      </Link>
      {HTTP_CONFIG.API.Consumer.useMocks && (
        <Link component={LinkUnstyled as React.ElementType} to={to(Path.AboutMocks)} $disableBorderBottom={true}>
          <Typography variant='labelM'>{t('about.change-mocked-data-settings')}</Typography>
          <FontAwesomeIcon icon={ICONS.farChevronRight} />
        </Link>
      )}
    </Stack>
  )
}
